import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  MDBAnimation,
  MDBBadge,
  MDBCard,
  MDBCardBody,
  MDBTable,
  MDBView,
} from "mdbreact";
import { GenericProducts } from "../../../../../../constants";
import TableRowCount from "../../../../../../components/pagination/rows";
import Pagination from "../../../../../../components/pagination";
import { CONSUMABLE_PHASES, getPhase } from "./phases";

/**
 * Read-only view of the constant generic products (constants/genericProducts.json).
 */
const Index = () => {
  const { maxPage = 5 } = useSelector(({ auth }) => auth),
    [search, setSearch] = useState(""),
    [phase, setPhase] = useState(""),
    // "" = all, "perTest" = declared per patient, "reusable" = monthly dispense
    [usage, setUsage] = useState(""),
    [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const key = search.trim().toLowerCase();
    return GenericProducts.active.filter(
      ({
        name = "",
        abbreviation = "",
        subname = "",
        category = "",
        section = "",
        phases = [],
        reusable,
      }) =>
        (!phase || phases.includes(phase)) &&
        (!usage || (usage === "reusable") === !!reusable) &&
        (!key ||
          [name, abbreviation, subname, category, section].some((value) =>
            value.toLowerCase().includes(key)
          ))
    );
  }, [search, phase, usage]);

  const totalPages = Math.ceil(filtered.length / maxPage) || 1;
  const activePage = Math.min(page, totalPages);
  const startIndex = (activePage - 1) * maxPage;
  const paginatedData = filtered.slice(startIndex, startIndex + maxPage);

  const handlePageChange = (action) => {
    const newPage =
      typeof action === "number" ? action : activePage + (action ? 1 : -1);
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  return (
    <MDBAnimation type="bounceInDown">
      <MDBCard narrow className="pb-3" style={{ minHeight: "600px" }}>
        <MDBView
          cascade
          className="gradient-card-header custom-header blue-gradient narrower py-2 mx-4 mb-3 d-flex justify-content-between align-items-center"
        >
          <span className="white-text mx-3 text-nowrap mt-0">
            {filtered.length} Generic Products
          </span>
          <div className="d-flex" style={{ gap: 8 }}>
            <select
              className="browser-default custom-select custom-select-sm"
              value={phase}
              onChange={({ target }) => {
                setPhase(target.value);
                setPage(1);
              }}
            >
              <option value="">All phases</option>
              {CONSUMABLE_PHASES.map(({ key, abbr, label }) => (
                <option key={key} value={key}>
                  {abbr} – {label}
                </option>
              ))}
            </select>
            <select
              className="browser-default custom-select custom-select-sm"
              value={usage}
              onChange={({ target }) => {
                setUsage(target.value);
                setPage(1);
              }}
            >
              <option value="">Per test & reusable</option>
              <option value="perTest">Per test only</option>
              <option value="reusable">Reusable only</option>
            </select>
            <input
              className="form-control form-control-sm"
              placeholder="Search..."
              value={search}
              onChange={({ target }) => {
                setSearch(target.value);
                setPage(1);
              }}
            />
          </div>
        </MDBView>

        <MDBCardBody>
          <MDBTable responsive hover bordered small>
            <thead>
              <tr>
                <th>ID</th>
                <th>Abbr</th>
                <th>Name</th>
                <th>Category</th>
                <th>Section</th>
                <th>Phase</th>
                <th>UOM</th>
                <th>Usage</th>
              </tr>
            </thead>
            <tbody>
              {!paginatedData.length && (
                <tr>
                  <td colSpan={8} className="text-center text-muted">
                    No generic products found.
                  </td>
                </tr>
              )}
              {paginatedData.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>
                    <code>{item.abbreviation}</code>
                  </td>
                  <td>
                    <strong>{item.name}</strong>
                    {item.subname && (
                      <small className="d-block text-muted">
                        {item.subname}
                      </small>
                    )}
                  </td>
                  <td>{item.category}</td>
                  <td>{item.section}</td>
                  <td>
                    {(item.phases || []).map((key) => {
                      const found = getPhase(key);
                      return (
                        found && (
                          <MDBBadge
                            key={key}
                            color={found.color}
                            className="mr-1"
                            title={found.label}
                          >
                            {found.abbr}
                          </MDBBadge>
                        )
                      );
                    })}
                  </td>
                  <td>{item.uom}</td>
                  <td>
                    {item.reusable ? (
                      <MDBBadge
                        color="light"
                        title="P.O. + monthly dispense by the Chief MLS"
                      >
                        Reusable
                      </MDBBadge>
                    ) : (
                      <MDBBadge
                        color="success"
                        title="Declared per test, brand required"
                      >
                        Per test
                      </MDBBadge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </MDBTable>
        </MDBCardBody>

        <div className="mb-auto d-flex justify-content-between align-items-center px-4">
          <TableRowCount disablePageSelect={false} />
          <Pagination
            total={totalPages}
            page={activePage}
            setPage={handlePageChange}
          />
        </div>
      </MDBCard>
    </MDBAnimation>
  );
};

export default Index;
